import * as React from "react";
import {
  Classes,
  Text,
  Tag,
  Button,
  ButtonGroup,
  Intent,
  FormGroup,
  Position,
  //   ProgressBar,
  Menu,
  HTMLTable,
  Alignment,
  H4,
} from "@blueprintjs/core";

import { Dimension } from "../../../types/models";

import { MenuItem2, Popover2 } from "@blueprintjs/popover2";
import MultiSelector from "../ChildSelector/MultiSelector";
import Sidebar from "../Sidebar";
import Loading from "src/components/molecules/Loading";
import classnames from "classnames";
import { Box, Flex } from "@rebass/grid";
import { map } from "lodash";
import { Select2 } from "@blueprintjs/select";

export const SELECT_ALL = "SELECT_ALL";
export const DESELECT_ALL = "DESELECT_ALL";

export type SELECT_CHILDREN = "SELECT_ALL" | "DESELECT_ALL" | "";

interface IProps {
  sideBarDimension: Dimension;
  attributes: any[];
  selectedAttributes: any[];
  handleSubmit: (
    selectedAttributes: any[],
    selectedItemsByAttribute?: any
  ) => void;
  isFetchAttributesLoading: boolean;
  sideBarType: "DIMENSION" | "DIMENSION_INSTANCE";
  onClose?: () => void;
  parentDimName: any;
}

interface IState {
  selectedInstances: any[];
  isChanged: boolean;
  selectAll: SELECT_CHILDREN;
  selectedInstanceIds: number[];
  showConfirmBox: string;
  attributeInstanceTableData: any[];
  selectedItemsByAttribute: {
    [key: string]: { label: string; value: string } | null;
  };
}

class ClientMasterConfigSidebar extends React.Component<IProps, IState> {
  public constructor(props: IProps) {
    super(props);
    this.state = {
      selectedInstances: [],
      isChanged: false,
      selectAll: "",
      selectedInstanceIds: [],
      showConfirmBox: "",
      attributeInstanceTableData: [],
      selectedItemsByAttribute: {},
    };
  }

  public componentDidMount() {
    this.initializeState();
  }

  private initializeState = () => {
    const { sideBarType, selectedAttributes, attributes } = this.props;

    if (sideBarType === "DIMENSION") {
      // For DIMENSION type: selectedAttributes are attribute mappings
      // Need to match them against the actual attributes by finding matching ones
      const selectedIds = selectedAttributes.map(
        (attr: any) => attr.ClientAttributeID || attr.id
      );
      
      const selectedInsts = attributes.filter((attr: any) => 
        selectedIds.includes(attr.id || attr.ClientAttributeID)
      );

      this.setState({
        selectedInstances: selectedInsts,
        selectedInstanceIds: selectedIds,
        isChanged: false,
      });
    } else if (sideBarType === "DIMENSION_INSTANCE") {
      // For DIMENSION_INSTANCE type: build attribute instance table
      const attributeInstanceTableData: any = {};
      const selectedItemsByAttribute: {
        [key: string]: { label: string; value: string } | null;
      } = {};

      // Group instances by parent attribute
      for (let i = 0; i < attributes.length; i++) {
        const attribute = attributes[i];
        const parentAttr = attribute.parentAttr;
        if (!attributeInstanceTableData[parentAttr]) {
          attributeInstanceTableData[parentAttr] = [attribute];
        } else {
          attributeInstanceTableData[parentAttr].push(attribute);
        }
      }

      // Find pre-selected items from selectedAttributes
      Object.entries(attributeInstanceTableData).forEach(
        ([attrName, instances]: [string, any]) => {
          const selectedInst = (instances as any[]).find((inst: any) =>
            selectedAttributes.some(
              (attr: any) => attr.ClientAttributeInstanceID === inst.id
            )
          );
          if (selectedInst) {
            selectedItemsByAttribute[attrName] = {
              label: selectedInst.Name,
              value: String(selectedInst.id || selectedInst.ClientAttributeID),
            };
          }
        }
      );

      this.setState({
        selectedInstances: selectedAttributes,
        selectedInstanceIds: selectedAttributes.map(
          (attr: any) => attr.ClientAttributeID || attr.id
        ),
        attributeInstanceTableData,
        selectedItemsByAttribute,
        isChanged: false,
      });
    }
  };

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    // Reinitialize when selected attributes change or sidebar type changes
    if (
      prevProps.selectedAttributes !== this.props.selectedAttributes ||
      prevProps.attributes !== this.props.attributes ||
      prevProps.sideBarType !== this.props.sideBarType
    ) {
      this.initializeState();
    }
  }

  public componentWillUnmount() {
    this.setState({
      selectedInstances: [],
      isChanged: false,
      selectAll: "",
      selectedInstanceIds: [],
      showConfirmBox: "",
      attributeInstanceTableData: [],
      selectedItemsByAttribute: {},
    });
  }

  public render() {
    const { sideBarType, isFetchAttributesLoading } = this.props;
    
    const headingText = sideBarType === "DIMENSION" 
      ? "Dimension - Attribute Mapping"
      : "DimensionInstance - AttributeInstance Mapping";

    return (
      <Sidebar>
        <div style={{ maxWidth: "400px", width: "100%", margin: "0 auto" }}>
          <Flex justifyContent="space-between" alignItems="center" mb={3}>
            <H4 style={{ margin: 0 }}>{headingText}</H4>
            <Button
              minimal
              small
              icon="cross"
              onClick={this.props.onClose}
              title="Close sidebar"
            />
          </Flex>
          
          {isFetchAttributesLoading ? (
            <Loading />
          ) : sideBarType === "DIMENSION" ? (
            this.renderDimensionSelector()
          ) : (
            this.renderAttributeInstanceSelector()
          )}
        </div>
      </Sidebar>
    );
  }

  private renderDimensionSelector = () => {
    const { sideBarDimension, attributes } = this.props;
    const { selectedInstanceIds, selectAll, isChanged } = this.state;

    return (
      <Flex flexDirection={"column"}>
        {this.displayDimensionName({
          name: sideBarDimension.Name,
        })}
        <FormGroup>
          <ButtonGroup fill={true}>
            <Button
              text="Set as Attribute"
              intent={Intent.PRIMARY}
              onClick={this.handleClick}
              disabled={!isChanged}
            />
            <Popover2
              position={Position.RIGHT}
              content={
                <Menu>
                  <MenuItem2
                    tagName="div"
                    text={"Select all"}
                    onClick={this.selectNodes}
                  />
                  <MenuItem2
                    tagName="div"
                    text={"Deselect all"}
                    onClick={this.deSelectNodes}
                  />
                </Menu>
              }
            >
              <Button intent={Intent.PRIMARY} icon="more" />
            </Popover2>
          </ButtonGroup>
          <MultiSelector
            key={sideBarDimension.id}
            instances={attributes}
            preSelectedInstanceIds={selectedInstanceIds}
            onNodeSelect={this.handleNodeSelect}
            dimensionName={sideBarDimension.Name}
            selectAll={selectAll}
            isChanged={isChanged}
            placeholder="Select Attributes..."
          />
        </FormGroup>
      </Flex>
    );
  };

  private renderAttributeInstanceSelector = () => {
    const { sideBarDimension } = this.props;
    const { attributeInstanceTableData } = this.state;

    return (
      <Flex flexDirection={"column"}>
        {this.displayInstanceName(
          this.props.parentDimName,
          sideBarDimension.Name,
        )}
        <HTMLTable style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th>
                <Box>
                  <Text className={classnames(Classes.TEXT_MUTED, Classes.TEXT_LARGE)}>
                    Attribute
                  </Text>
                </Box>
              </th>
              <th>
                <Box>
                  <Text className={classnames(Classes.TEXT_MUTED, Classes.TEXT_LARGE)}>
                    Attribute Instance
                  </Text>
                </Box>
              </th>
            </tr>
          </thead>
          <tbody>
            {map(
              Object.entries(attributeInstanceTableData),
              ([attrName, instances]) => this.renderAttributeInstanceRow(attrName, instances)
            )}
          </tbody>
        </HTMLTable>
        <Button
          intent={Intent.PRIMARY}
          onClick={this.handleClick}
          fill
          disabled={!Object.keys(attributeInstanceTableData).length}
          style={{ marginTop: "12px" }}
        >
          Set as Attribute Instance
        </Button>
      </Flex>
    );
  };

  private renderAttributeInstanceRow = (attrName: string, instances: any) => {
    return (
      <tr
        style={{
          borderBottom: "1px solid #E1E8ED",
          width: "100%",
        }}
        key={attrName}
      >
        <td>
          <Flex alignItems="center" css={{ height: "48px", width: "100%" }}>
            <Text>{attrName}</Text>
          </Flex>
        </td>
        <td>
          <FormGroup label="" labelFor="output-tags-select">
            <Select2<{ label: string; value: string }>
              items={instances.map((tag: any) => ({
                label: tag.Name,
                value: String(tag.id),
              }))}
              noResults={<MenuItem2 disabled={true} text="No results." />}
              itemListRenderer={(itemListProps) => (
                <Menu ulRef={itemListProps.itemsParentRef}>
                  {map(itemListProps.items, (item, index) =>
                    itemListProps.renderItem(item, index)
                  )}
                </Menu>
              )}
              itemRenderer={(item, { modifiers, handleClick }) => (
                <MenuItem2
                  active={modifiers.active}
                  key={item.value}
                  onClick={handleClick}
                  text={item.label}
                />
              )}
              onItemSelect={(item) => {
                this.setState((prevState) => ({
                  selectedItemsByAttribute: {
                    ...prevState.selectedItemsByAttribute,
                    [attrName]: item,
                  },
                }));
              }}
              filterable={false}
              popoverProps={{ matchTargetWidth: true }}
            >
              <Button
                fill={true}
                alignText={Alignment.LEFT}
                rightIcon="caret-down"
                text={this.state.selectedItemsByAttribute[attrName]?.label || "Select..."}
              />
            </Select2>
          </FormGroup>
        </td>
      </tr>
    );
  };

  private selectNodes = () => {
    const allInstanceIds = this.props.attributes.map(
      (attr: any) => attr.id || attr.ClientAttributeID
    );
    this.setState({
      selectAll: SELECT_ALL,
      isChanged: true,
      selectedInstanceIds: allInstanceIds,
      selectedInstances: this.props.attributes,
    });
  };

  private deSelectNodes = () => {
    this.setState({
      selectAll: DESELECT_ALL,
      isChanged: true,
      selectedInstanceIds: [],
      selectedInstances: [],
    });
  };

  private handleNodeSelect = (instances: any[]) => {
    const selectedIds = instances.map(
      (instance: any) => instance.id || instance.ClientAttributeID
    );
    this.setState({
      selectedInstances: instances,
      selectedInstanceIds: selectedIds,
      isChanged: true,
      selectAll: "",
    });
  };

  private handleClick = () => {
    this.setState({ isChanged: true });
    this.props.handleSubmit(
      this.state.selectedInstances,
      this.state.selectedItemsByAttribute
    );
  };

  private displayDimensionName = (props: any) => {
    return (
      <Flex className={Classes.TEXT_LARGE} alignItems={"center"} mb={3}>
        <Tag minimal={true}>
          <Text>{props.name}</Text>
        </Tag>
      </Flex>
    );
  };

  private displayInstanceName = (dimName: string, instName: string) => {
    return (
      <Flex className={Classes.TEXT_LARGE} alignItems={"center"} mb={3}>
        <Tag minimal={true}>
          <Text>{`${dimName} -> ${instName}`}</Text>
        </Tag>
      </Flex>
    );
  };
}

export default ClientMasterConfigSidebar;
